import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Loader2, Check, X, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

const DAY_ORDER = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

export default function SondageHoraires() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);

  const load = () => axios.get(`${API}/survey/schedule`, { withCredentials: true })
    .then((r) => setData(r.data))
    .catch(() => setData({ slots: [], answered: 0, total: 0 }));

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    load();
  }, [isAuthenticated]);

  const vote = async (slot, value) => {
    const next = slot.my_vote === value ? null : value;
    setSaving(slot.slot_key);
    try {
      await axios.post(`${API}/survey/schedule/vote`, { slot_key: slot.slot_key, value: next }, { withCredentials: true });
      setData((d) => ({
        ...d,
        slots: d.slots.map((s) => s.slot_key === slot.slot_key ? { ...s, my_vote: next } : s),
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Une erreur est survenue.');
    } finally {
      setSaving(null);
    }
  };

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F5EFE5]"><Loader2 className="w-6 h-6 animate-spin text-[#C99A55]" /></div>;
  }

  const answered = data.slots.filter((s) => s.my_vote).length;
  const groups = [
    { key: 'planned', title: 'Créneaux prévus', sub: 'Dites-nous ceux qui vous conviennent', slots: data.slots.filter((s) => s.planned) },
    { key: 'candidate', title: 'Créneaux proposés', sub: 'Aidez-nous à décider de nouveaux horaires', slots: data.slots.filter((s) => !s.planned) },
  ];

  return (
    <div className="min-h-screen bg-[#F5EFE5] pb-24" data-testid="sondage-page">
      <div className="max-w-3xl mx-auto px-4 pt-10">
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#C99A55] font-semibold mb-2">Votre avis compte</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-[#493329] mb-3">Sondage horaires</h1>
        <p className="text-[#7A6455] max-w-2xl text-sm sm:text-base">
          Aidez-nous à construire le planning idéal du studio PURO. Indiquez pour chaque créneau s'il vous conviendrait.
          Vos réponses sont modifiables à tout moment.
        </p>
        <div className="mt-4 inline-flex items-center gap-2 text-sm text-[#493329] bg-white border border-[#E9DDCC] rounded-full px-4 py-1.5" data-testid="sondage-progress">
          <CalendarClock className="w-4 h-4 text-[#C99A55]" />
          {answered} / {data.slots.length} créneaux renseignés
        </div>

        {groups.map((g) => (
          <div key={g.key} className="mt-10">
            <h2 className="font-heading text-lg text-[#493329]">{g.title}</h2>
            <p className="text-xs text-[#7A6455] mb-4">{g.sub}</p>
            <div className="space-y-2.5">
              {g.slots
                .slice()
                .sort((a, b) => DAY_ORDER.indexOf(a.day) - DAY_ORDER.indexOf(b.day) || a.start.localeCompare(b.start))
                .map((s) => (
                <div key={s.slot_key} className="flex items-center gap-3 bg-white border border-[#E9DDCC] rounded-2xl px-4 py-3" data-testid={`sondage-slot-${s.slot_key}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#493329]">{s.day} · {s.start} – {s.end}</p>
                    <p className="text-xs text-[#7A6455] truncate">{s.type}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => vote(s, 'oui')}
                      disabled={saving === s.slot_key}
                      data-testid={`vote-oui-${s.slot_key}`}
                      className={`flex items-center gap-1 rounded-full px-3.5 py-2 text-sm transition-all ${s.my_vote === 'oui' ? 'bg-[#8C9B86] text-white' : 'bg-[#F0E8D9] text-[#493329] hover:bg-[#8C9B86]/20'}`}>
                      <Check className="w-4 h-4" /> Oui
                    </button>
                    <button
                      onClick={() => vote(s, 'non')}
                      disabled={saving === s.slot_key}
                      data-testid={`vote-non-${s.slot_key}`}
                      className={`flex items-center gap-1 rounded-full px-3.5 py-2 text-sm transition-all ${s.my_vote === 'non' ? 'bg-[#C76542] text-white' : 'bg-[#F0E8D9] text-[#493329] hover:bg-[#C76542]/20'}`}>
                      <X className="w-4 h-4" /> Non
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
